import { defineStore } from 'pinia';
import client from '../modules/client';

export const userRegTypes = {
    REGUSER: "RegisteredUser",
    ATTORNEY: "Attorney",
    AGENCY: "Agency",
    PARTYONCASE: "PartyOnCase"
}

export const useUserStore = defineStore("user", {

    state: () => ({
        userTypes: [],
        agencyTypes: [],
        user: {
            userType: null,
            status: 0,
            firstName: null,
            middleName: null,
            lastName: null,
            email: null,
            emailConfirmed: false,
            address: null,
            city: null,
            state: null,
            zip: null,
            phone: null,
            altPhone: null,
            documentsUploaded: false,
            documentTypes: [],
            attorneyFields: {
                barNumber: null
            },
            partyToCaseFields: {
                dlNumber: null,
                cases: null,
            },
            agencyFields: {
                agencyType: null
            },
            signatureLink: null,
            username: null
        },
        userRegistrationKey: null,
        existingUserRegistrationKey: null,
        error: null,
        adding: false,
        editing: false,
        userConfig: null,
        agencyConfig: null,
        config: null,
        currentUserConfig: null,
        documentReady: false,
        docType: null,
        newPassword: null,
        confirmedPassword: null
    }),
    
    actions: {
        async register() {

            try {
                this.clear();
                this.setAddMode(false);

                let data = this.user;

                data.captchaToken = await client.validateCaptcha("sc_homepage");

                //validate user, check if already exists
                let existingUserResponse = await client.get(`/sci/userregistration/registrationbyemail/${this.user.email}`);
                this.existingUserRegistrationKey = existingUserResponse.data;

                if (this.existingUserRegistrationKey) {
                    //Reload user
                    await this.loadUser(this.existingUserRegistrationKey);
                }

                else {
                    let response = await client.post(`/sci/userregistration/register`, data);
                    this.userRegistrationKey = response.data;
                }
                
            }
            catch (e) {
                console.error(e);
                this.error = 'User Registration Failed. ' + e.message;
                this.setAddMode(true);
            }
        },

        async updateRegistration(confID) {

            try {
                this.error = '';
                this.setEditMode(false);

                let data = this.user;

                data.captchaToken = await client.validateCaptcha("sc_homepage");

                let response = await client.post(`/sci/userregistration/register/${confID}`, data);

                //Reload user
                await this.loadUser(confID);
            }
            catch (e) {
                console.error(e);

                this.error = 'Could not update the user registration. ' + e.message;
                this.setEditMode(true);
            }
        },

        async loadUser(confID) {

            try {
                let response = await client.get(`/sci/userregistration/userregistrationinfo/${confID}`);
                this.user = response.data;

                if (this.userConfig && this.user) {

                    let config = this.userConfig;

                    switch (this.user.userType) {
                        case userRegTypes.REGUSER:
                            this.currentUserConfig = config.registereduser;
                            break;
                        case userRegTypes.ATTORNEY:
                            this.currentUserConfig = config.attorney;
                            break;
                        case userRegTypes.AGENCY:
                            this.currentUserConfig = config.agency;
                            break;
                        case userRegTypes.PARTYONCASE:
                            this.currentUserConfig = config.partytoacase;
                            break;
                    }

                    //get doc type for the current user
                    let docType;
                    if (this.currentUserConfig) {
                        docType = this.currentUserConfig.doctype;

                        //check if user is agency
                        let agencytypes = this.currentUserConfig.agencytypes;
                        let currentUserAgency = this.user.agencyFields;

                        if (agencytypes && currentUserAgency && currentUserAgency.agencyType) {
                            Object.keys(agencytypes).forEach(function (agency) {
                                if (agency.toLowerCase() === currentUserAgency.agencyType.toLowerCase()) {
                                    docType = agencytypes[agency].doctype;
                                }
                            });
                        }
                    }

                    this.docType = docType;
                    console.log('doctype = ' + this.docType);
                }
            }
            catch (e) {
                console.error(e);
                this.error = 'Could not get the user information. ' +  e.message;
            }
        },

        async uploadDocument(formData, confID) {

            try {
                this.error = '';

                let response = await client.post(`/sci/userregistration/uploadregistrationdocs/${confID}/${this.docType}`, formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                this.loadUser(confID);
            }
            catch (e) {
                console.error(e);
                this.error = 'Failed to upload documents. ' + e.message;
            }
        },

        setAddMode(value) {
            this.adding = value;
        },

        setEditMode(value) {
            this.editing = value;
        },

        async cancelUserChanges(confID) {
            this.setEditMode(false);
            await this.loadUser(confID);
        },

        clear() {
            this.error = '';
            this.userRegistrationKey = '';
            this.existingUserRegistrationKey = '';
            this.currentUserConfig = null;
        },

        async loadUserConfig() {

            let response = await client.get(`/uiconfigs/usertypes.json`);

            if (response.status >= 200 && response.status <= 299 && typeof response.data === 'object') {

                let types = response.data.usertypes;
                this.userConfig = types;

                let users = [];
                let agencies = [];
                let agencytypes;

                Object.keys(types).forEach(function (userType) {

                    let value;

                    switch (userType.toLowerCase()) {
                        case "registereduser":
                            value = userRegTypes.REGUSER;
                            break;
                        case "attorney":
                            value = userRegTypes.ATTORNEY;
                            break;
                        case "agency":
                            value = userRegTypes.AGENCY;
                            agencytypes  = types[userType].agencytypes;

                            if (agencytypes) {
                                Object.keys(agencytypes).forEach(function (agency) {
                                    agencies.push(agency);
                                });
                            }

                            break;
                        case "partytoacase":
                            value = userRegTypes.PARTYONCASE;
                            break;
                        default:
                            break;
                    }

                    users.push({
                        value: value,
                        text: types[userType].text
                    });
                });

                this.userTypes = { ...users };
                this.agencyConfig = agencytypes;
                this.agencyTypes = { ...agencies };
            }
        },

        async loadConfig() {

            let response = await client.get(`/uiconfigs/config.json`);

            if (response.status >= 200 && response.status <= 299 && typeof response.data === 'object') {
                this.config = response.data.config;
            }
        },

        async loadCurrentUser(confID) {

            //load user config
            await this.loadUserConfig();

            //confirm email
            await this.confirmEmail(confID);

            //load user
            await this.loadUser(confID);
        },

        async confirmEmail(confID) {

            try {
                await client.get(`/sci/userregistration/confirmregistration/${confID}`);
            }
            catch (e) {
                console.error(e);
                this.error = 'User could not be confirmed. ' + e.message;
            }
        },

        async sendConfirmationLink(confID) {

            try {

                let response = await client.get(`/sci/userregistration/confirmationlink/${confID}`);

                this.clear();

                this.userRegistrationKey = response.data;
            }
            catch (e) {
                console.error(e);
                this.error = 'Could not send the confirmation link. ' + e.message;
            }
        },

        async isDocumentReady(confID) {

            try {
                const checkDocumentReady = async (value) => {

                    console.log('checkDocumentReady = ' + value);

                    if (value > 0) {

                        let response = await client.get(`/sci/userregistration/isdocumentready/${confID}`);

                        let ready = response.data;

                        if (ready) {
                            this.documentReady = true;
                            return;
                        }

                        await new Promise(resolve => setTimeout(() => resolve(), 5000));

                        await checkDocumentReady(value - 1);
                    }
                }

                await checkDocumentReady(10);
            }
            catch (e) {
                console.error(e);
                this.error = 'Could not check the document status. ' + e.message;
            }
        },

        async isDocumentSigned(confID) {

            try {
                this.error = '';

                let response = await client.get(`/sci/userregistration/isdocumentsigned/${confID}`);

                if (response.data === false)
                    this.error = 'Document must be signed to continue.';

                return response.data;
            }
            catch (e) {
                console.error(e);
                this.error = 'Could not check the document status. ' + e.message;
            }
        },
        
        async requestPasswordReset(username) {

            let captchaToken = await client.validateCaptcha("sc_homepage");

            const request = {
                username,
                captchaToken
            };

            let result = await client.post(`/sci/account/resetpasswordbyemail`, request)

            return result.data.email.email;
        },

        async validateToken(token) {

            try {
                this.error = '';

                let response = await client.get(`/sci/account/validatetoken/${token}`);

                if (response.data === false)
                    this.error = 'Email link is invalid or has expired.';

                return response.data;
            }
            catch (e) {
                console.error(e);
                this.error = 'Email link is invalid or has expired. ' + e.message;
            }
        },

        async resetPasswordFromToken(userIdToken, newPassword, confirmPassword) {

            const request = {
                userIdToken,
                newPassword,
                confirmPassword
            };

            await client.post(`/sci/account/resetpasswordfromtoken`, request)
        }
    }
});